import { ref, reactive, onActivated, nextTick } from 'vue';
import { ElMessage, ElMessageBox } from 'element-plus';
import { Plus, Edit, Delete, View } from '@element-plus/icons-vue';
import { request } from '@/util/globalContext';
import { listCommon } from '@/util/vueutil/commonList.js';

export default function useExchangeItemList() {
  const listRef = ref(null);
  const dialogVisible = ref(false);
  const dialogType = ref('add');
  const formRef = ref(null);

  const searchForm = reactive({
    itemName: '',
    status: '',
    exchangeType: ''
  });

  const {
    listShow,
    handleReset,
    handleSelectionChange,
    handleCurrentRowChange
  } = listCommon(searchForm);

  const FORM_DEFAULT = {
    id: '',
    itemId: '',
    itemName: '',
    itemValue: 0,
    requiredPoints: 0,
    expressSupport: '0',
    quantity: 0,
    status: '1',
    exchangeType: '0'
  };

  const formData = reactive({ ...FORM_DEFAULT });

  const formRules = {
    itemName: [
      { required: true, message: '请输入物品名称', trigger: 'blur' }
    ],
    requiredPoints: [
      { required: true, message: '请输入所需积分', trigger: 'blur' }
    ],
    status: [
      { required: true, message: '请选择状态', trigger: 'change' }
    ]
  };

  const handleLoadList = async (paginationParams) => {
    if (!listRef.value) return;
    if (!listRef.value.isInited) {
      await nextTick();
      if (!listRef.value?.isInited) {
        ElMessage.warning('列表组件初始化中，请稍后重试');
        return;
      }
    }

    listRef.value?.setLoading(true);
    try {
      const params = {
        ...paginationParams,
        itemName: searchForm.itemName,
        status: searchForm.status,
        exchangeType: searchForm.exchangeType
      };
      const res = await request.get('/exchange/item/getPage', { params });
      if (listRef.value) {
        if (res.code === 200) {
          listRef.value.updateListData(res.data.records || []);
          listRef.value.updatePagination({
            pageNum: paginationParams.pageNum,
            pageSize: paginationParams.pageSize,
            total: res.data.total || 0
          });
        } else {
          ElMessage.error('列表加载失败：' + (res.msg || '接口异常'));
          listRef.value.updateListData([]);
          listRef.value.updatePagination({ ...paginationParams, total: 0 });
        }
      }
    } catch (error) {
      ElMessage.error('列表加载失败：网络异常');
      if (listRef.value) {
        listRef.value.updateListData([]);
        listRef.value.updatePagination({ ...paginationParams, total: 0 });
      }
    } finally {
      listRef.value?.setLoading(false);
    }
  };

  const handleAdd = async () => {
    dialogType.value = 'add';
    formRef.value?.resetFields();
    Object.assign(formData, JSON.parse(JSON.stringify(FORM_DEFAULT)));
    dialogVisible.value = true;
  };

  const handleEdit = async () => {
    if (!listShow.value) {
      ElMessage.warning('请先选择一条数据！');
      return;
    }

    try {
      listRef.value?.setLoading(true);
      const res = await request.get('/exchange/item/getDetail', { params: { id: listShow.value.id } });
      if (res.code !== 200) {
        ElMessage.error(res.msg || '接口异常');
        return;
      }

      formData.id = res.data.id || '';
      formData.itemId = res.data.itemId || '';
      formData.itemName = res.data.itemName || '';
      formData.itemValue = res.data.itemValue ?? 0;
      formData.requiredPoints = res.data.requiredPoints ?? 0;
      formData.expressSupport = res.data.expressSupport || '0';
      formData.quantity = res.data.quantity ?? 0;
      formData.status = res.data.status || '1';
      formData.exchangeType = res.data.exchangeType || '0';

      dialogType.value = 'edit';
      dialogVisible.value = true;
    } catch (error) {
      ElMessage.error('获取详情失败：' + error);
    } finally {
      listRef.value?.setLoading(false);
    }
  };

  const handleView = async () => {
    if (!listShow.value) {
      ElMessage.warning('请先选择一条数据！');
      return;
    }

    try {
      listRef.value?.setLoading(true);
      const res = await request.get('/exchange/item/getDetail', { params: { id: listShow.value.id } });
      if (res.code !== 200) {
        ElMessage.error(res.msg || '接口异常');
        return;
      }

      formData.id = res.data.id || '';
      formData.itemId = res.data.itemId || '';
      formData.itemName = res.data.itemName || '';
      formData.itemValue = res.data.itemValue ?? 0;
      formData.requiredPoints = res.data.requiredPoints ?? 0;
      formData.expressSupport = res.data.expressSupport || '0';
      formData.quantity = res.data.quantity ?? 0;
      formData.status = res.data.status || '1';
      formData.exchangeType = res.data.exchangeType || '0';

      dialogType.value = 'view';
      dialogVisible.value = true;
    } catch (error) {
      ElMessage.error('获取详情失败：' + error);
    } finally {
      listRef.value?.setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!listShow.value) {
      ElMessage.warning('请先选择数据！');
      return;
    }

    try {
      await ElMessageBox.confirm('确定删除选中的数据？', '删除确认', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      });

      listRef.value?.setLoading(true);
      const params = { id: listShow.value.id };
      const res = await request.post('/exchange/item/delete', params);
      if (listRef.value) {
        if (res.code === 200) {
          ElMessage.success('删除成功');
          const pagination = listRef.value.getPagination();
          handleLoadList(pagination);
        } else {
          ElMessage.error('删除失败：' + (res.msg || '接口异常'));
        }
      }
    } catch (error) {
      if (error !== 'cancel') {
        ElMessage.error('删除失败：网络异常');
      }
    } finally {
      listRef.value?.setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!formRef.value) return;

    const isValid = await new Promise((resolve) => {
      formRef.value.validate((valid) => {
        resolve(valid);
      });
    });
    if (!isValid) return;

    const submitData = {
      id: dialogType.value === 'edit' ? formData.id : '',
      itemName: formData.itemName,
      itemValue: formData.itemValue,
      requiredPoints: formData.requiredPoints,
      expressSupport: formData.expressSupport,
      quantity: formData.quantity,
      status: formData.status,
      exchangeType: formData.exchangeType
    };

    listRef.value?.setLoading(true);
    try {
      const res = await request.post('/exchange/item/saveOrUpdate', submitData);
      if (listRef.value) {
        if (res.code === 200) {
          ElMessage.success(dialogType.value === 'add' ? '新增成功' : '修改成功');
          dialogVisible.value = false;
          const pagination = listRef.value.getPagination();
          handleLoadList(pagination);
        } else {
          const tip = dialogType.value === 'add' ? '新增失败' : '修改失败';
          ElMessage.error(tip + '：' + (res.msg || '接口异常'));
        }
      }
    } catch (error) {
      ElMessage.error('接口异常：' + error);
    } finally {
      listRef.value?.setLoading(false);
    }
  };

  onActivated(async () => {
    if (listRef.value?.isInited) {
      const pagination = listRef.value.getPagination();
      handleLoadList(pagination);
    }
  });

  return {
    searchForm,
    listRef,
    dialogVisible,
    dialogType,
    formRef,
    listShow,
    formData,
    formRules,
    Plus,
    Edit,
    Delete,
    View,
    handleSelectionChange,
    handleCurrentRowChange,
    handleAdd,
    handleEdit,
    handleView,
    handleDelete,
    handleLoadList,
    handleReset,
    handleSubmit
  };
}
